import { Add } from "@mui/icons-material";
import { Fab, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { parseAsString } from "nuqs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { GqlPermissions } from "~/__generated__";
import GaiaDataList from "~/modules/app/components/GaiaDataList";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import GaiaTableLinkCell from "~/modules/app/components/GaiaTableLinkCell";
import useClientOrProjectScope from "~/modules/app/hooks/useClientOrProjectScope";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";
import SearchInput from "~/shared/components/SearchInput";
import { useDebouncedValue } from "~/shared/hooks/useDebouncedValue";
import { useGridQueryState } from "~/shared/hooks/useGridQueryState";

import useProtocolListRoute_GetAll from "./api/useProtocolListRoute_GetAll";

export default function ProtocolListRoute() {
    const { t } = useTranslation("protocol");
    const { client, project } = useClientOrProjectScope();
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    const [queryState, setQueryState, gridModelProps] = useGridQueryState({
        q: parseAsString.withDefault(""),
    });

    const debouncedQ = useDebouncedValue(queryState.q);

    const params = useMemo(
        () => ({
            clientId: client?.id,
            projectId: project?.id,
            q: debouncedQ || undefined,
            page: queryState.page,
            pageSize: queryState.pageSize,
            sortBy: queryState.sortBy,
            sortOrder: queryState.sortOrder,
        }),
        [client?.id, project?.id, debouncedQ, queryState],
    );

    const { data, loading, error, total } = useProtocolListRoute_GetAll(params);

    const handleParameterChange = <T,>(key: string, value: T) => {
        setQueryState({ [key]: value, page: 0 } as Parameters<typeof setQueryState>[0]);
    };

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "name",
                headerName: t("protocolDataGrid.headerName"),
                flex: 1,
            },
            {
                type: "actions",
                field: "id",
                headerName: "",
                maxWidth: 40,
                renderCell: props => <GaiaTableLinkCell value={props.value} />,
            },
        ],
        [t],
    );

    const title = client
        ? t("protocolListScreen.clientTitle")
        : project
          ? t("protocolListScreen.projectTitle")
          : t("protocolListScreen.title");

    if (error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <GaiaPageGridComponent
                title={title}
                actions={
                    isAdmin ? (
                        <Fab component={Link} to="new" color="primary">
                            <Add />
                        </Fab>
                    ) : undefined
                }
            >
                <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                    <SearchInput
                        label={t("protocolDataGrid.filterSearch")}
                        value={queryState.q}
                        onChange={value => handleParameterChange("q", value)}
                    />
                </Stack>

                <GaiaDataList
                    data={data || []}
                    loading={loading}
                    error={error}
                    totalRows={total || 0}
                    columns={columns}
                    gridModels={gridModelProps}
                />
            </GaiaPageGridComponent>
        </GaiaPageLayoutColumn>
    );
}
